import { useCallback, useEffect, useState } from 'react';

import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import { useKeyPosePersistenceActions } from '../hooks/useKeyPosePersistenceActions';
import { useStore as useKeyPoseStore } from '../keyPoseStore';

const UNSUPPORTED_CHARACTER_MESSAGE =
  'Motion editing is only supported with Axel right now, please change to Axel to continue.';

export default function UnsupportedCharacterKeyPoseModal() {
  const keyPoseStore = useKeyPoseStore();
  const motionEditorStore = useMotionEditorStore();
  const { saveCurrentKeyPoses } = useKeyPosePersistenceActions();
  const [changingCharacter, setChangingCharacter] = useState(false);
  const prompt = keyPoseStore.state.unsupportedCharacterKeyPosePrompt;

  useEffect(() => {
    if (!prompt) {
      setChangingCharacter(false);
    }
  }, [prompt]);

  const handleCancel = useCallback(() => {
    if (!prompt || changingCharacter) {
      return;
    }

    keyPoseStore.clearKeyPoseEditingSelection();
    keyPoseStore.removeKeyPose(prompt.keyPoseId);
    keyPoseStore.clearUnsupportedCharacterKeyPosePrompt(prompt.keyPoseId);
  }, [changingCharacter, keyPoseStore, prompt]);

  const handleChangeCharacter = useCallback(async () => {
    if (!prompt || changingCharacter) {
      return;
    }

    setChangingCharacter(true);
    const didChangeCharacters =
      await motionEditorStore.changeAllSceneCharactersToDefault();
    if (!didChangeCharacters) {
      setChangingCharacter(false);
      return;
    }

    keyPoseStore.clearUnsupportedCharacterKeyPosePrompt(prompt.keyPoseId);
    setChangingCharacter(false);
  }, [changingCharacter, keyPoseStore, motionEditorStore, prompt]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  if (!prompt) {
    return null;
  }

  return (
    <Modal
      title="Motion editing unavailable"
      description={UNSUPPORTED_CHARACTER_MESSAGE}
      maxWidth={440}
      open
      closeOnClickOutside={false}
      onOpenChange={handleOpenChange}
      footer={
        <div className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            disabled={changingCharacter}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={changingCharacter}
            onClick={() => {
              void handleChangeCharacter();
            }}
          >
            Change character
          </Button>
        </div>
      }
    >
      <div />
    </Modal>
  );
}
